import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Building2, Phone, Mail, MapPin, DollarSign, Trash2, Plus, Loader2,
  Calendar, MessageSquare, Car, Trophy, Briefcase, Award, X,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "new",        label: "New",         color: "bg-blue-500/10 border-blue-500/30 text-blue-300" },
  { key: "visited",    label: "Visited",     color: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300" },
  { key: "qualified",  label: "Qualified",   color: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
  { key: "quote_sent", label: "Quote Sent",  color: "bg-violet-500/10 border-violet-500/30 text-violet-300" },
  { key: "won",        label: "Won",         color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
  { key: "lost",       label: "Lost",        color: "bg-rose-500/10 border-rose-500/30 text-rose-300" },
];

const ACT_ICONS = {
  visit: Building2, call: Phone, email: Mail, meeting: Calendar, quote: DollarSign, note: MessageSquare,
};

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actOpen, setActOpen] = useState(false);
  const [vehOpen, setVehOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/sales/prospects/${id}`); setP(r.data); }
    catch { toast.error("Prospect not found"); navigate("/sales"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const setStatus = async (newStatus) => {
    try {
      await api.patch(`/sales/prospects/${id}`, { status: newStatus });
      toast.success(`Status: ${STATUSES.find((s) => s.key === newStatus)?.label}`);
      if (newStatus === "won") setConvertOpen(true);
      load();
    } catch { toast.error("Update failed"); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this prospect?")) return;
    await api.delete(`/sales/prospects/${id}`);
    toast.success("Archived");
    navigate("/sales");
  };

  if (loading || !p) {
    return <div className="flex items-center justify-center min-h-[60vh]" data-testid="prospect-detail-loading"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  }
  const statusConf = STATUSES.find((s) => s.key === p.status) || STATUSES[0];

  return (
    <div className="space-y-5" data-testid="prospect-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/sales" className="text-slate-400 hover:text-emerald-300" data-testid="back-to-pipeline"><ArrowLeft className="w-5 h-5" /></Link>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{p.type?.replace(/_/g, " ")}</div>
            <h1 className="text-2xl font-bold text-white">{p.company_name}</h1>
            {p.contact_name && <div className="text-sm text-slate-400 mt-0.5">{p.contact_name}{p.contact_role ? ` · ${p.contact_role}` : ""}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${statusConf.color} px-3 py-1.5 text-sm`} data-testid="prospect-status-badge">{statusConf.label}</Badge>
          <select
            value={p.status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
            data-testid="prospect-status-select"
          >
            {STATUSES.map((s) => <option key={s.key} value={s.key}>Move to: {s.label}</option>)}
          </select>
          {p.status === "won" && !p.converted_customer_id && (
            <Button onClick={() => setConvertOpen(true)} className="bg-emerald-600 hover:bg-emerald-500" data-testid="convert-btn">
              <Trophy className="w-4 h-4 mr-2" /> Convert to Customer
            </Button>
          )}
          {p.converted_customer_id && (
            <Badge className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 flex items-center gap-1.5">
              <Award className="w-3 h-3" /> Converted
            </Badge>
          )}
          <Button variant="outline" onClick={archive} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid="archive-prospect-btn"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Info */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Contact & Address</div>
          <dl className="space-y-2 text-sm">
            {p.phone && <Row icon={Phone}>{p.phone}</Row>}
            {p.email && <Row icon={Mail}>{p.email}</Row>}
            {(p.address || p.city) && <Row icon={MapPin}>{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")}</Row>}
            {p.website && <Row icon={Building2}><a href={p.website} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">{p.website}</a></Row>}
          </dl>
          {(p.estimated_volume || p.estimated_value) && (
            <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-2 gap-3 text-xs">
              {p.estimated_volume && <div><div className="text-slate-500">Volume/month</div><div className="text-slate-200 font-medium text-sm mt-0.5">{p.estimated_volume} cars</div></div>}
              {p.estimated_value && <div><div className="text-slate-500">Pipeline value</div><div className="text-emerald-400 font-medium text-sm mt-0.5">${Number(p.estimated_value).toLocaleString()}</div></div>}
            </div>
          )}
          {p.notes && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Notes</div>
              <p className="text-sm text-slate-300 whitespace-pre-line">{p.notes}</p>
            </div>
          )}
        </div>

        {/* Activities */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-200 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-400" /> Activity Timeline</div>
            <Button size="sm" onClick={() => setActOpen(true)} className="bg-cyan-600 hover:bg-cyan-500" data-testid="add-activity-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Log Activity
            </Button>
          </div>
          <div className="space-y-2.5">
            {(p.activities || []).length === 0 ? (
              <div className="text-sm text-slate-500 italic text-center py-6">No activities yet. Log your first visit/call/email.</div>
            ) : p.activities.map((a) => {
              const Icon = ACT_ICONS[a.type] || MessageSquare;
              return (
                <div key={a.id} className="rounded-lg border border-slate-800 bg-slate-900/30 p-3" data-testid={`activity-${a.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 text-cyan-400" />
                      <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px] uppercase tracking-wider">{a.type}</Badge>
                      <span className="text-sm text-slate-200 font-medium">{a.title || a.type.charAt(0).toUpperCase() + a.type.slice(1)}</span>
                      {a.quote_amount ? <Badge className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 text-[10px]">${Number(a.quote_amount).toLocaleString()}</Badge> : null}
                    </div>
                    <button onClick={async () => { if (!window.confirm("Remove this activity?")) return; await api.delete(`/sales/prospects/${id}/activities/${a.id}`); toast.success("Removed"); load(); }} className="text-slate-600 hover:text-rose-400" data-testid={`del-activity-${a.id}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                  {a.notes && <p className="text-xs text-slate-400 mt-2 whitespace-pre-line">{a.notes}</p>}
                  {a.next_action && <div className="mt-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-2.5 py-1.5 text-[11px] text-amber-300">⏭ {a.next_action}{a.next_action_date ? ` — ${a.next_action_date}` : ""}</div>}
                  <div className="text-[10px] text-slate-500 mt-2">{a.occurred_at?.slice(0, 16).replace("T", " ")} · by {a.created_by || "system"}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Vehicles Planned */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="planned-vehicles">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-200 flex items-center gap-2"><Car className="w-4 h-4 text-violet-400" /> Vehicles Planned ({(p.vehicles || []).length})</div>
          <Button size="sm" onClick={() => setVehOpen(true)} className="bg-violet-600 hover:bg-violet-500" data-testid="add-vehicle-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Vehicle
          </Button>
        </div>
        {(p.vehicles || []).length === 0 ? (
          <p className="text-sm text-slate-500 italic">No vehicles planned. Add VINs the customer expects to send for calibration — they'll become Jobs automatically when converted.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {p.vehicles.map((v) => (
              <div key={v.id} className="rounded-lg border border-slate-800 bg-slate-900/30 p-3 group" data-testid={`vehicle-${v.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 font-medium">{[v.year, v.make, v.model].filter(Boolean).join(" ") || "—"}</div>
                    {v.vin && <div className="text-[10px] text-slate-500 font-mono mt-0.5">{v.vin}</div>}
                    {v.expected_service_date && <div className="text-[11px] text-amber-300 mt-1">📅 {v.expected_service_date}</div>}
                    {v.estimated_value && <div className="text-[11px] text-emerald-400 mt-0.5">${Number(v.estimated_value).toLocaleString()}</div>}
                  </div>
                  <button onClick={async () => { if (!window.confirm("Remove this vehicle?")) return; await api.delete(`/sales/prospects/${id}/vehicles/${v.id}`); toast.success("Removed"); load(); }} className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-rose-400" data-testid={`del-vehicle-${v.id}`}><X className="w-3.5 h-3.5" /></button>
                </div>
                {v.notes && <p className="text-[11px] text-slate-400 mt-1.5">{v.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <ActivityDialog
        key={`act-${actOpen ? "o" : "c"}`}
        open={actOpen}
        prospectId={id}
        onClose={() => setActOpen(false)}
        onSaved={() => { setActOpen(false); load(); }}
      />
      <VehicleDialog
        key={`veh-${vehOpen ? "o" : "c"}`}
        open={vehOpen}
        prospectId={id}
        onClose={() => setVehOpen(false)}
        onSaved={() => { setVehOpen(false); load(); }}
      />
      <ConvertDialog
        key={`conv-${convertOpen ? "o" : "c"}`}
        open={convertOpen}
        prospect={p}
        onClose={() => setConvertOpen(false)}
        onConverted={(result) => { setConvertOpen(false); load(); toast.success(`${result.jobs_created.length} job(s) created in Operations`); }}
      />
    </div>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-slate-300 text-sm">
      <Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" /> <span className="truncate">{children}</span>
    </div>
  );
}

function ActivityDialog({ open, onClose, prospectId, onSaved }) {
  const [form, setForm] = useState({ type: "visit", title: "", notes: "", outcome: "", quote_amount: "", next_action: "", next_action_date: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, quote_amount: form.quote_amount ? parseFloat(form.quote_amount) : null };
      await api.post(`/sales/prospects/${prospectId}/activities`, body);
      toast.success("Activity logged");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl bg-[#0B1120] border-slate-800" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-400" /> Log Activity</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Track every interaction with this prospect.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field2 label="Type">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="h-10 w-full px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
              data-testid="act-type">
              <option value="visit">Visit (in-person)</option>
              <option value="call">Phone Call</option>
              <option value="email">Email</option>
              <option value="meeting">Meeting</option>
              <option value="quote">Quote Sent</option>
              <option value="note">Note</option>
            </select>
          </Field2>
          <Field2 label="Title">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-[#030712] border-slate-800" placeholder="Met with Mike (owner)" data-testid="act-title" />
          </Field2>
          <Field2 label="Notes / Conversation">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-[#030712] border-slate-800 min-h-[100px]"
              placeholder="What was discussed, pain points, decision makers, objections..."
              data-testid="act-notes" />
          </Field2>
          {form.type === "quote" && (
            <Field2 label="Quote Amount $">
              <Input type="number" value={form.quote_amount} onChange={(e) => setForm({ ...form, quote_amount: e.target.value })}
                className="bg-[#030712] border-slate-800 font-mono" placeholder="15000" data-testid="act-quote" />
            </Field2>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field2 label="Next Action">
              <Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                className="bg-[#030712] border-slate-800" placeholder="Send pricing PDF" data-testid="act-next-action" />
            </Field2>
            <Field2 label="Date">
              <Input type="date" value={form.next_action_date} onChange={(e) => setForm({ ...form, next_action_date: e.target.value })}
                className="bg-[#030712] border-slate-800" data-testid="act-next-date" />
            </Field2>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-500" data-testid="act-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VehicleDialog({ open, onClose, prospectId, onSaved }) {
  const [form, setForm] = useState({ vin: "", year: "", make: "", model: "", expected_service_date: "", estimated_value: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, year: form.year ? parseInt(form.year, 10) : null, estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null };
      await api.post(`/sales/prospects/${prospectId}/vehicles`, body);
      toast.success("Vehicle added");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl bg-[#0B1120] border-slate-800" data-testid="vehicle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Car className="w-4 h-4 text-violet-400" /> Add Vehicle Planned</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Add a VIN that this customer will send for calibration.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field2 label="VIN" span2><Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} className="bg-[#030712] border-slate-800 font-mono" maxLength={17} data-testid="vf-vin" /></Field2>
          <Field2 label="Year"><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bg-[#030712] border-slate-800 font-mono" /></Field2>
          <Field2 label="Make"><Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="bg-[#030712] border-slate-800" /></Field2>
          <Field2 label="Model" span2><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-[#030712] border-slate-800" /></Field2>
          <Field2 label="Expected Date"><Input type="date" value={form.expected_service_date} onChange={(e) => setForm({ ...form, expected_service_date: e.target.value })} className="bg-[#030712] border-slate-800" /></Field2>
          <Field2 label="Est. Value $"><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-[#030712] border-slate-800 font-mono" /></Field2>
          <Field2 label="Notes" span2><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#030712] border-slate-800 min-h-[60px]" /></Field2>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-500" data-testid="vf-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConvertDialog({ open, onClose, prospect, onConverted }) {
  const [createJobs, setCreateJobs] = useState(true);
  const [saving, setSaving] = useState(false);
  const vehicleCount = (prospect?.vehicles || []).length;

  const doConvert = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/sales/prospects/${prospect.id}/convert`, { create_jobs: createJobs });
      onConverted?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#0B1120] border-slate-800" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-400" /> Convert to Customer</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Move this prospect into your operational customer base.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-sm text-slate-300">
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
            ✓ <span className="font-medium">{prospect?.company_name}</span> will be added to Master Data → Customers.
          </div>
          <label className="flex items-start gap-2 rounded-md border border-slate-800 bg-slate-900/40 p-3 cursor-pointer">
            <input type="checkbox" checked={createJobs} onChange={(e) => setCreateJobs(e.target.checked)} className="mt-0.5 accent-emerald-500" data-testid="convert-create-jobs" />
            <div>
              <div className="text-sm text-slate-100">Create {vehicleCount} Job{vehicleCount !== 1 ? "s" : ""} in Operations</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Each planned vehicle becomes a Job with status &quot;quoted&quot;, ready for scheduling.</div>
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button onClick={doConvert} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="convert-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5 mr-1.5" />}Convert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field2({ label, children, span2 }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
